import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { subscribeNewsletter } from '../services/leadService';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import api from '../services/api';

const { FiCheckCircle, FiAlertCircle, FiBookOpen } = FiIcons;

const formatArticleDate = (isoDate) => new Date(isoDate).toLocaleDateString(undefined, { month: 'short', year: 'numeric' });

export default function Insights() {
  const [articles, setArticles] = useState([]);
  const [articlesLoading, setArticlesLoading] = useState(true);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [subscribeStatus, setSubscribeStatus] = useState(null);

  useEffect(() => {
    api.getPublicArticles()
      .then(res => setArticles(res.articles || []))
      .catch(err => console.error('Error loading articles:', err))
      .finally(() => setArticlesLoading(false));
  }, []);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email || loading) return;

    setLoading(true);
    setSubscribeStatus(null);

    try {
      await subscribeNewsletter(email);
      setSubscribeStatus({ type: 'success', message: 'You have been subscribed to Maxmark Intelligence Briefs.' });
      setEmail('');
    } catch (err) {
      setSubscribeStatus({ type: 'error', message: err.message || 'Subscription failed. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-black pt-40 pb-32">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-4xl mb-32">
          <span className="inline-block py-1 px-3 rounded-full bg-blue-600/10 border border-blue-600/20 text-blue-500 text-[10px] font-bold tracking-[0.2em] uppercase mb-8">
            Knowledge & Signals
          </span>
          <h1 className="text-6xl md:text-8xl font-serif font-bold mb-10 text-white leading-[1.1]">
            Intelligence for Better Marketing Decisions.
          </h1>
          <p className="text-2xl text-zinc-400 font-light leading-relaxed">
            Practical analysis of advertising, creativity, consumer behaviour, and African market signals.
          </p>
        </motion.div>

        {articlesLoading ? (
          <p className="text-zinc-500 text-sm mb-40">Loading briefs...</p>
        ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-40">
          {articles.map((article, i) => (
            <motion.div
              key={article.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className="group p-10 bg-zinc-950 border border-zinc-900 rounded-[2.5rem] hover:border-blue-600 transition-all flex flex-col justify-between"
            >
              <div>
                <div className="flex justify-between items-center mb-8">
                  <span className="text-[10px] font-bold uppercase tracking-widest text-blue-500">{article.category}</span>
                  <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-600">{formatArticleDate(article.published_at)}</span>
                </div>
                <h3 className="text-2xl font-serif font-bold mb-6 text-white group-hover:text-blue-500 transition-colors">
                  {article.title}
                </h3>
                <p className="text-zinc-500 text-sm leading-relaxed mb-8">{article.excerpt}</p>
              </div>
              <button
                onClick={() => setSelectedArticle(article)}
                className="text-[10px] font-bold uppercase tracking-widest text-white group-hover:text-blue-400 flex items-center gap-2 transition-all self-start"
              >
                Read Article &rarr;
              </button>
            </motion.div>
          ))}
          {articles.length === 0 && (
            <p className="text-zinc-500 text-sm col-span-full">New briefs are on the way. Check back soon.</p>
          )}
        </div>
        )}

        {/* Newsletter Subscription Box */}
        <section className="bg-zinc-950 border border-zinc-900 p-12 md:p-24 rounded-[3.5rem] text-center relative overflow-hidden shadow-2xl">
          <div className="max-w-2xl mx-auto relative z-10">
            <h2 className="text-4xl md:text-5xl font-serif font-bold mb-6 text-white">Get Intelligence Briefs Directly.</h2>
            <p className="text-lg text-zinc-400 mb-10 leading-relaxed font-light">
              Join forward-thinking marketing leaders who receive our bi-weekly breakdown of competitor ad signals and market opportunities.
            </p>

            <form onSubmit={handleSubscribe} className="max-w-md mx-auto space-y-4">
              <div className="flex flex-col sm:flex-row gap-3">
                <input 
                  type="email" 
                  value={email} 
                  onChange={(e) => setEmail(e.target.value)} 
                  placeholder="Enter your work email" 
                  className="input-field flex-grow" 
                  required 
                />
                <button 
                  type="submit" 
                  disabled={loading}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3.5 rounded-xl font-bold uppercase tracking-widest text-[10px] transition-all disabled:opacity-50 shrink-0 shadow-lg shadow-blue-600/20"
                >
                  {loading ? 'Subscribing...' : 'Subscribe'}
                </button>
              </div>

              <AnimatePresence>
                {subscribeStatus && (
                  <motion.div 
                    initial={{ opacity: 0, y: -5 }} 
                    animate={{ opacity: 1, y: 0 }} 
                    exit={{ opacity: 0 }} 
                    className={`p-3 rounded-xl text-xs flex items-center justify-center gap-2 ${
                      subscribeStatus.type === 'success' 
                        ? 'bg-emerald-900/20 text-emerald-400 border border-emerald-900/40' 
                        : 'bg-red-900/20 text-red-400 border border-red-900/40'
                    }`}
                  >
                    <SafeIcon icon={subscribeStatus.type === 'success' ? FiCheckCircle : FiAlertCircle} className="w-4 h-4" />
                    {subscribeStatus.message}
                  </motion.div>
                )}
              </AnimatePresence>
            </form>
          </div>
        </section>

        {/* Article Reading Modal */}
        <AnimatePresence>
          {selectedArticle && (
            <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
              <motion.div 
                initial={{ opacity: 0, scale: 0.95 }} 
                animate={{ opacity: 1, scale: 1 }} 
                exit={{ opacity: 0, scale: 0.95 }} 
                className="bg-zinc-950 border border-zinc-800 rounded-3xl max-w-2xl w-full p-8 md:p-12 shadow-2xl relative max-h-[90vh] overflow-y-auto"
              >
                <div className="flex justify-between items-center mb-6">
                  <span className="text-[10px] font-bold uppercase tracking-widest text-blue-500">{selectedArticle.category}</span>
                  <button 
                    onClick={() => setSelectedArticle(null)}
                    className="text-zinc-500 hover:text-white text-lg font-bold p-2"
                  >
                    ✕
                  </button>
                </div>

                <h2 className="text-3xl md:text-4xl font-serif font-bold text-white mb-6 leading-tight">
                  {selectedArticle.title}
                </h2>
                <div className="text-sm text-zinc-400 leading-relaxed space-y-4 mb-8">
                  <p>{selectedArticle.content || selectedArticle.excerpt}</p>
                  <p>
                    Maxmark monitors these movements across Meta, Google, and TikTok ad channels in Nigeria and the broader African market, producing decision-ready briefing packs for leadership teams.
                  </p>
                </div>

                <div className="pt-6 border-t border-zinc-900 flex justify-between items-center">
                  <span className="text-xs text-zinc-600">{formatArticleDate(selectedArticle.published_at)}</span>
                  <button 
                    onClick={() => setSelectedArticle(null)}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2.5 rounded-xl text-xs font-bold uppercase tracking-wider"
                  >
                    Done Reading
                  </button>
                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}